import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  timesheetService,
  leaveService,
  performanceReviewService,
  visaService,
  notificationService,
  userService,
  reportService,
} from '../../services/api';
import {
  FiClock,
  FiCalendar,
  FiStar,
  FiShield,
  FiUsers,
  FiLayers,
  FiBell,
  FiTrendingUp,
  FiChevronRight,
  FiAlertCircle,
} from 'react-icons/fi';
import { format } from 'date-fns';
import './Dashboard.css';

const Dashboard = () => {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Employee State
  const [hoursThisWeek, setHoursThisWeek] = useState(0);
  const [leaveBalance, setLeaveBalance] = useState(0);
  const [pendingReviews, setPendingReviews] = useState(0);
  const [visaStatus, setVisaStatus] = useState('Valid');
  const [recentTimesheets, setRecentTimesheets] = useState([]);
  const [upcomingLeaves, setUpcomingLeaves] = useState([]);
  const [recentNotifications, setRecentNotifications] = useState([]);

  // Admin State
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [totalDepartments, setTotalDepartments] = useState(0);
  const [onLeaveToday, setOnLeaveToday] = useState(0);
  const [expiringVisas, setExpiringVisas] = useState(0);
  const [recentLeaveRequests, setRecentLeaveRequests] = useState([]);

  // Fetch employee dashboard data
  const fetchEmployeeDashboard = async () => {
    try {
      setError('');

      // Fetch hours this week
      const timesheetsResponse = await timesheetService.getAll({
        limit: 5,
      });
      const timesheets = timesheetsResponse.data.timesheets || [];
      setRecentTimesheets(timesheets);

      const totalHours = timesheets.reduce(
        (sum, ts) => sum + (parseFloat(ts.totalHours) || 0),
        0
      );
      setHoursThisWeek(totalHours);

      // Fetch leave balance
      const balanceResponse = await leaveService.getBalance(user.id);
      const balanceData = balanceResponse.data;
      const totalRemaining = Array.isArray(balanceData)
        ? balanceData.reduce((sum, b) => sum + (b.remainingDays || 0), 0)
        : 0;
      setLeaveBalance(totalRemaining);

      // Fetch pending reviews
      const reviewsResponse = await performanceReviewService.getAll({
        limit: 10,
      });
      setPendingReviews(reviewsResponse.data.total || 0);

      // Fetch visa status
      try {
        const visaResponse = await visaService.getMy();
        const visa = visaResponse.data;
        if (visa && visa.expiryDate) {
          const expiryDate = new Date(visa.expiryDate);
          const today = new Date();
          const daysUntilExpiry = Math.floor(
            (expiryDate - today) / (1000 * 60 * 60 * 24)
          );

          if (daysUntilExpiry < 0) {
            setVisaStatus('Expired');
          } else if (daysUntilExpiry < 30) {
            setVisaStatus(`Expiring in ${daysUntilExpiry} days`);
          } else {
            setVisaStatus('Valid');
          }
        }
      } catch (err) {
        console.error('Visa fetch error:', err);
        setVisaStatus('Not available');
      }

      // Fetch upcoming leaves
      const leavesResponse = await leaveService.getAll({
        status: 'approved',
        limit: 3,
      });
      setUpcomingLeaves(leavesResponse.data.leaves || []);

      // Fetch recent notifications
      const notificationsResponse = await notificationService.getAll({
        limit: 3,
        unreadOnly: true,
      });
      setRecentNotifications(notificationsResponse.data.notifications || []);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load dashboard data');
    }
  };

  // Fetch admin dashboard data
  const fetchAdminDashboard = async () => {
    try {
      setError('');

      // Fetch total employees
      const usersResponse = await userService.getAll({ limit: 1 });
      setTotalEmployees(usersResponse.data.total || 0);

      // Fetch total departments
      const deptResponse = await reportService.getHeadcount();
      setTotalDepartments(deptResponse.data.byDepartment?.length || 0);

      // Fetch on leave today
      const leaveResponse = await leaveService.getAll({
        status: 'approved',
        limit: 100,
      });
      const today = new Date().toISOString().split('T')[0];
      const onLeaveCount = (leaveResponse.data.leaves || []).filter(
        (leave) =>
          leave.startDate <= today && leave.endDate >= today
      ).length;
      setOnLeaveToday(onLeaveCount);

      // Fetch expiring visas
      const expiringResponse = await visaService.getExpiring(30);
      const expiringData = expiringResponse.data;
      setExpiringVisas(Array.isArray(expiringData) ? expiringData.length : 0);

      // Fetch recent leave requests
      const recentResponse = await leaveService.getAll({
        limit: 5,
      });
      setRecentLeaveRequests(recentResponse.data.leaves || []);
    } catch (err) {
      console.error('Admin dashboard fetch error:', err);
      setError('Failed to load admin dashboard data');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (isAdmin) {
        await fetchAdminDashboard();
      } else {
        await fetchEmployeeDashboard();
      }
      setLoading(false);
    };

    fetchData();
  }, [isAdmin, user?.id]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {error && (
        <div className="alert alert-danger">
          <FiAlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Welcome Section */}
      <div className="welcome-section">
        <h1>Welcome back, {user?.firstName}!</h1>
        <p>
          {isAdmin
            ? 'Here is your company overview'
            : 'Here is your personal overview'}
        </p>
      </div>

      {/* Stats Cards */}
      {!isAdmin ? (
        // Employee Stats
        <div className="stats-grid">
          <div className="stat-card primary">
            <div className="stat-header">
              <FiClock size={24} />
              <span className="stat-label">Hours This Week</span>
            </div>
            <div className="stat-value">{hoursThisWeek.toFixed(1)}</div>
            <div className="stat-subtitle">hours logged</div>
          </div>

          <div className="stat-card success">
            <div className="stat-header">
              <FiCalendar size={24} />
              <span className="stat-label">Leave Balance</span>
            </div>
            <div className="stat-value">{leaveBalance}</div>
            <div className="stat-subtitle">days remaining</div>
          </div>

          <div className="stat-card warning">
            <div className="stat-header">
              <FiStar size={24} />
              <span className="stat-label">Pending Reviews</span>
            </div>
            <div className="stat-value">{pendingReviews}</div>
            <div className="stat-subtitle">awaiting acknowledgment</div>
          </div>

          <div className="stat-card danger">
            <div className="stat-header">
              <FiShield size={24} />
              <span className="stat-label">Visa Status</span>
            </div>
            <div className="stat-value-text">{visaStatus}</div>
            <div className="stat-subtitle">current status</div>
          </div>
        </div>
      ) : (
        // Admin Stats
        <div className="stats-grid">
          <div className="stat-card primary">
            <div className="stat-header">
              <FiUsers size={24} />
              <span className="stat-label">Total Employees</span>
            </div>
            <div className="stat-value">{totalEmployees}</div>
            <div className="stat-subtitle">in organization</div>
          </div>

          <div className="stat-card success">
            <div className="stat-header">
              <FiLayers size={24} />
              <span className="stat-label">Departments</span>
            </div>
            <div className="stat-value">{totalDepartments}</div>
            <div className="stat-subtitle">total departments</div>
          </div>

          <div className="stat-card warning">
            <div className="stat-header">
              <FiCalendar size={24} />
              <span className="stat-label">On Leave Today</span>
            </div>
            <div className="stat-value">{onLeaveToday}</div>
            <div className="stat-subtitle">employees</div>
          </div>

          <div className="stat-card danger">
            <div className="stat-header">
              <FiShield size={24} />
              <span className="stat-label">Expiring Visas</span>
            </div>
            <div className="stat-value">{expiringVisas}</div>
            <div className="stat-subtitle">within 30 days</div>
          </div>
        </div>
      )}

      {/* Content Sections */}
      <div className="dashboard-content">
        {!isAdmin ? (
          // Employee Sections
          <>
            {/* Recent Timesheets */}
            <div className="dashboard-section">
              <div className="section-header">
                <h2>Recent Timesheets</h2>
                <Link to="/timesheet" className="view-all">
                  View All <FiChevronRight size={18} />
                </Link>
              </div>

              {recentTimesheets.length > 0 ? (
                <div className="recent-list">
                  {recentTimesheets.slice(0, 5).map((timesheet) => (
                    <Link
                      key={timesheet.id}
                      to={`/timesheet/${timesheet.date}`}
                      className="recent-item"
                    >
                      <div className="recent-item-info">
                        <div className="recent-item-title">
                          {format(new Date(timesheet.date), 'MMM dd, yyyy')}
                        </div>
                        <div className="recent-item-meta">
                          <span className="recent-hours">
                            {timesheet.totalHours} hours
                          </span>
                          <span className={`recent-status ${timesheet.status}`}>
                            {timesheet.status}
                          </span>
                        </div>
                      </div>
                      <FiChevronRight size={20} />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No timesheets yet</p>
                </div>
              )}
            </div>

            {/* Upcoming Leaves */}
            <div className="dashboard-section">
              <div className="section-header">
                <h2>Upcoming Leaves</h2>
                <Link to="/leave" className="view-all">
                  View All <FiChevronRight size={18} />
                </Link>
              </div>

              {upcomingLeaves.length > 0 ? (
                <div className="recent-list">
                  {upcomingLeaves.slice(0, 5).map((leave) => (
                    <div key={leave.id} className="recent-item">
                      <div className="recent-item-info">
                        <div className="recent-item-title">
                          {leave.LeaveType?.name}
                        </div>
                        <div className="recent-item-meta">
                          <span className="recent-date">
                            {format(new Date(leave.startDate), 'MMM dd')} -{' '}
                            {format(new Date(leave.endDate), 'MMM dd, yyyy')}
                          </span>
                        </div>
                      </div>
                      <span className="badge badge-success">Approved</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No upcoming leaves</p>
                </div>
              )}
            </div>

            {/* Recent Notifications */}
            <div className="dashboard-section">
              <div className="section-header">
                <h2>Recent Notifications</h2>
                <Link to="/notifications" className="view-all">
                  View All <FiChevronRight size={18} />
                </Link>
              </div>

              {recentNotifications.length > 0 ? (
                <div className="recent-list">
                  {recentNotifications.slice(0, 3).map((notification) => (
                    <div
                      key={notification.id}
                      className={`recent-item ${notification.isRead ? '' : 'unread'}`}
                    >
                      <div className="recent-item-info">
                        <div className="recent-item-title">
                          {notification.title}
                        </div>
                        <div className="recent-item-meta">
                          <span className="recent-date">
                            {format(
                              new Date(notification.createdAt),
                              'MMM dd, HH:mm'
                            )}
                          </span>
                        </div>
                      </div>
                      {!notification.isRead && (
                        <span className="notification-dot"></span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No notifications</p>
                </div>
              )}
            </div>
          </>
        ) : (
          // Admin Sections
          <>
            {/* Quick Actions */}
            <div className="dashboard-section">
              <h2>Quick Actions</h2>
              <div className="action-buttons">
                <Link to="/admin/employees/new" className="btn btn-primary">
                  <FiUsers size={18} />
                  Add Employee
                </Link>
                <Link to="/admin/reports" className="btn btn-secondary">
                  <FiTrendingUp size={18} />
                  View Reports
                </Link>
              </div>
            </div>

            {/* Recent Leave Requests */}
            <div className="dashboard-section">
              <div className="section-header">
                <h2>Recent Leave Requests</h2>
                <Link to="/leave" className="view-all">
                  Manage <FiChevronRight size={18} />
                </Link>
              </div>

              {recentLeaveRequests.length > 0 ? (
                <div className="recent-list">
                  {recentLeaveRequests.slice(0, 5).map((leave) => (
                    <div key={leave.id} className="recent-item">
                      <div className="recent-item-info">
                        <div className="recent-item-title">
                          {leave.User?.firstName} {leave.User?.lastName}
                        </div>
                        <div className="recent-item-meta">
                          <span className="recent-date">
                            {leave.LeaveType?.name} -{' '}
                            {format(new Date(leave.startDate), 'MMM dd')} to{' '}
                            {format(new Date(leave.endDate), 'MMM dd')}
                          </span>
                        </div>
                      </div>
                      <span className="badge badge-warning">Pending</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No pending leave requests</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
